import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import OrderItem from '../OrderItem';
import Checkbox from '@afosto/app-components-react/Checkbox';
import DataList from '@afosto/app-components-react/DataList';
import Pagination from '@afosto/app-components-react/Pagination';
import SelectionBar from '@afosto/app-components-react/SelectionBar';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import translations from './translations';

const OrderItemsList = ({
  dataSource,
  onItemQuantity,
  onRemoveItems,
  allowNegativeQuantity,
  isVatShifted,
  isIncludingVat,
}) => {
  const intl = useIntl();
  const { data, pagination: paginationModule = {}, selection: selectionModule = {} } = dataSource;
  const {
    allItemsSelected,
    setDeselectedItems,
    setSelectedItems,
    deselectItems,
    selectItems,
    deselectedItems = [],
    selectedItems = [],
  } = selectionModule;
  const {
    activePage,
    getNextPageCursor,
    goToPage,
    itemCount,
    itemsPerPage = 10,
    itemsPerPageOptions,
    setItemsPerPage,
  } = paginationModule;
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const itemIds = data.map(item => item.id);

  /**
   * Re-calculates whether the row selection checkbox should be checked or be indeterminate.
   */
  useEffect(() => {
    const selectedRowsOnPage = selectedItems.filter(itemId => itemIds.includes(itemId));

    let indeterminate =
      selectedRowsOnPage.length > 0 && itemIds.length - selectedRowsOnPage.length > 0;
    let checked = selectedRowsOnPage.length > 0 && selectedRowsOnPage.length === itemIds.length;

    if (allItemsSelected) {
      const deselectedRowsOnPage = deselectedItems.filter(itemId => itemIds.includes(itemId));

      indeterminate =
        deselectedRowsOnPage.length > 0 && itemIds.length - deselectedRowsOnPage.length > 0;
      checked = deselectedRowsOnPage.length === 0;
    }

    setIsIndeterminate(indeterminate);
    setIsChecked(checked);
  }, [itemIds, selectedItems, deselectedItems, allItemsSelected]);

  /**
   * Handle the change page event. Sets the active page.
   * @param {Object} event
   * @param {number} page
   */
  const handleChangePage = (event, page) => {
    goToPage(page);
  };

  /**
   * Handle the change rows per page event. Sets the rows per page.
   * @param {number} event
   */
  const handleChangeRowsPerPage = event => {
    setItemsPerPage(parseInt(event.target.value, 10));
  };

  const handleRemoveItems = async () => {
    if (onRemoveItems) {
      await onRemoveItems(selectedItems, dataSource);
    }

    setSelectedItems([]);
  };

  const handleSelect = selectAll => () => {
    if (selectAll) {
      selectItems(itemIds);
      setDeselectedItems(deselectedItems.filter(itemId => !itemIds.includes(itemId)));
    } else {
      setSelectedItems(selectedItems.filter(itemId => !itemIds.includes(itemId)));
      deselectItems(itemIds);
    }
  };

  const renderOrderItem = (item, { SelectionComponent }) => (
    <OrderItem
      key={item?.id}
      item={item}
      allowNegativeQuantity={allowNegativeQuantity}
      disableQuantity={selectedItems?.length > 0}
      onQuantity={onItemQuantity}
      SelectionComponent={SelectionComponent}
      isIncludingVat={isIncludingVat}
      isVatShifted={isVatShifted}
    />
  );

  const renderOrderItemSkeleton = key => (
    <ListItem key={key}>
      <ListItemText>
        <SkeletonBody lines={2} />
      </ListItemText>
    </ListItem>
  );

  const bulkActions = [
    {
      key: 'removeItems',
      render: () => (
        <Button onClick={handleRemoveItems} key="removeItems">
          <Typography variant="button">
            {intl.formatMessage(translations.actions.remove.label)}
          </Typography>
        </Button>
      ),
    },
  ];

  return (
    <>
      {selectedItems?.length > 0 && (
        <SelectionBar
          selectionCount={selectedItems.length}
          bulkActions={bulkActions}
          onClearSelection={() => setSelectedItems([])}
        />
      )}
      {dataSource?.data?.length > 0 && (
        <>
          <Box px={2}>
            <Checkbox
              name="selectAll"
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={handleSelect(!isChecked || isIndeterminate)}
              register={false}
            />
          </Box>
          <Divider />
        </>
      )}
      <DataList
        dataSource={dataSource}
        renderItem={renderOrderItem}
        renderSkeletonItem={renderOrderItemSkeleton}
        SelectionBarComponent={null}
        emptyMessage={intl.formatMessage(translations.emptyMessage)}
        emptyMessageAlignment="left"
        viewPagination={false}
      />
      {data?.length > 0 && (
        <Pagination
          getNextPageCursor={getNextPageCursor}
          itemCount={itemCount}
          itemsPerPage={itemsPerPage}
          itemsPerPageOptions={itemsPerPageOptions}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          page={activePage}
        />
      )}
    </>
  );
};

OrderItemsList.propTypes = {
  allowNegativeQuantity: PropTypes.bool,
  dataSource: PropTypes.object,
  isIncludingVat: PropTypes.bool,
  isVatShifted: PropTypes.bool,
  onItemQuantity: PropTypes.func,
  onRemoveItems: PropTypes.func,
};

OrderItemsList.defaultProps = {
  allowNegativeQuantity: true,
  dataSource: undefined,
  isIncludingVat: true,
  isVatShifted: false,
  onItemQuantity: undefined,
  onRemoveItems: undefined,
};

export default OrderItemsList;
